// --- Core ---
import {FORM_ERROR, setIn} from "final-form";
import {each} from "underscore";
// --- SaniQ ---
import {parsePagination} from "api";
import {deserializeContent, deserializeItem} from "../models";
import {DeserializedRecord, Deserializer, isJSONAPICollection, JSONAPIContent} from "../models/Helpers";
import {mapErrorRequestStatus, SubmitErrorKey} from "helpers/forms/SubmitError";
import {toCamel} from "helpers/StringUtil";

interface Errors {
  [k: string]: string[] | SubmitErrorKey;
}

/**
 * Convert the snake_case attribute names from Rails into camelCase
 *
 * @deprecated use nestifyErrors instead, to be used with Final Form
 */
const camelizeErrors = (errors: Errors): Errors => {
  const result: Errors = {};

  each(errors, (error, key) => {
    result[toCamel(key)] = error;
  });

  return result;
};

/**
 * Final Form uses a nested object structure. Whereas rails validations have dot separated paths
 *
 * This method converts keys to camelCase and nests the errors and returns a final form compatible object
 */
const nestifyErrors = (errors: Errors): Errors => {
  let result: Errors = {};

  each(errors, (error, key) => {
    result = setIn(result, toCamel(key), error) as Errors;
  });

  return result;
};

const handleSubmitError = (errorHandler = nestifyErrors) => ({content, status}): Errors => {
  if (status === 422) {
    return errorHandler ? errorHandler(content.errors) : content.errors;
  } else {
    return {
      [FORM_ERROR]: mapErrorRequestStatus(status)
    };
  }
};

interface APIResponse<T = JSONAPIContent> {
  content: T;
  status: number;
  header: Response['headers'];
}

interface Pagination {
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
}

interface HandledResponse<T extends DeserializedRecord | DeserializedRecord[]> {
  content: T;
  pagination: Pagination;
  status: APIResponse['status'];
}

type ResponseHandler<T extends DeserializedRecord | DeserializedRecord[]> = (response: APIResponse) => HandledResponse<T>;
function handleResponse<T extends DeserializedRecord>(deserializer?: Deserializer<T>, isCollection?: false): ResponseHandler<T>;
function handleResponse<T extends DeserializedRecord>(deserializer: Deserializer<T>, isCollection: true): ResponseHandler<Array<T>>;

function handleResponse<T extends DeserializedRecord = DeserializedRecord>(
  deserializer: Deserializer<T> = deserializeItem<T>,
  isCollection: boolean = false
) {
  return (response: APIResponse): HandledResponse<T> => {
    let pagination;

    if (isCollection) {
      try {
        pagination = parsePagination(response.header);
      } catch {
        const {content} = response;

        if (!isJSONAPICollection(content)) {
          throw "Expected collection, but did not find data as array";
        }

        //NOTE(Keune): Fallback when there was no pagination headers
        pagination = {
          total: content.data.length,
          perPage: content.data.length,
          totalPages: 1,
          page: 0
        };
      }
    }
    performance.mark("deserializeContent_Start");
    const content = deserializeContent<T>(response.content, deserializer);
    performance.measure("deserializeContent", "deserializeContent_Start");
    return {
      content,
      pagination,
      status: response.status
    };
  };
}

export {
  APIResponse,
  camelizeErrors,
  handleSubmitError,
  handleResponse,
  HandledResponse,
  nestifyErrors
};
